import React, { useMemo, useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Badge,
  UncontrolledTooltip,
  Button,
} from "reactstrap"
import { Link } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import CardPagination from "../../components/Common/CardPagination"
import swal from "sweetalert"
import { Oval } from "react-loader-spinner"
import { getAllPayments, delPayement } from "helpers/requests" // Adjust import as needed

const Payments = (props) => {
  useEffect(() => {
    document.title = "Payments"
  }, [])

  const [payments, setPayments] = useState([])
  const [filteredPayments, setFilteredPayments] = useState([])

  // Enhanced filtering states
  const [typeFilter, setTypeFilter] = useState(() => localStorage.getItem("pay_type") || "all")
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("pay_search") || "")
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("pay_sort") || "newest")

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem("pay_pageSize") || "12", 10))

  useEffect(() => {
    localStorage.setItem("pay_type", typeFilter)
    localStorage.setItem("pay_search", searchTerm)
    localStorage.setItem("pay_sort", sortBy)
    localStorage.setItem("pay_pageSize", String(pageSize))
  }, [typeFilter, searchTerm, sortBy, pageSize])

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(1)
  }, [typeFilter, searchTerm, sortBy, pageSize])
  const [isLoading, setIsLoading] = useState(true)

  // Derive unique payment types from the data
  const paymentTypes = useMemo(() => {
    const types = new Set(payments.map(payment => payment.type))
    return ["all", ...types]
  }, [payments])

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const fetchedPayments = await getAllPayments()
        setPayments(fetchedPayments)
        setFilteredPayments(fetchedPayments)
      } catch (error) {
        console.error("Error fetching payments:", error)
        swal("Error", "Unable to fetch payments. Please try again later.", "error")
      } finally {
        setIsLoading(false)
      }
    }

    fetchPayments()
  }, [])

  // Advanced filtering effect
  useEffect(() => {
    let filtered = [...payments]

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(payment => payment.type === typeFilter)
    }

    // Search filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase()
      filtered = filtered.filter(payment => 
        payment.firstname.toLowerCase().includes(searchTermLower) ||
        payment.lastname.toLowerCase().includes(searchTermLower) ||
        payment.email?.toLowerCase().includes(searchTermLower) ||
        payment.phonenumber.toLowerCase().includes(searchTermLower) ||
        payment.type.toLowerCase().includes(searchTermLower)
      )
    }

    // Sorting
    if (sortBy === "newest") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    } else if (sortBy === "oldest") {
      filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    } else if (sortBy === "highest") {
      filtered.sort((a, b) => Number(b.amount) - Number(a.amount))
    } else if (sortBy === "lowest") {
      filtered.sort((a, b) => Number(a.amount) - Number(b.amount))
    }

    setFilteredPayments(filtered)
  }, [payments, typeFilter, searchTerm, sortBy])

  const handleDelete = (payment) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this payment!",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await delPayement(payment.id)
          const fetchedPayments = await getAllPayments()
          setPayments(fetchedPayments)

          swal("Le paiement a été supprimé.", {
            icon: "success",
          })
        } catch (error) {
          console.error("Error deleting the payment:", error)
          swal("Error", "Unable to delete the payment. Please try again later.", "error")
        }
      } else {
        swal("Deletion canceled")
      }
    })
  }

  const handleCheckPaymentDetails = (payment) => {
    window.location.href = `/payments/${payment.id}`;
  };


  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={props.t("Payments")} breadcrumbItem={props.t("Payment List")} />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Payments</CardTitle>
                
                {/* Filtering and Sorting Row */}
                <Row className="mb-3">
                  <Col md="4">
                    <FormGroup>
                      <Label for="typeFilter">Filter by Type</Label>
                      <Input
                        type="select"
                        id="typeFilter"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                      >
                        {paymentTypes.map((type) => (
                          <option key={type} value={type}>
                            {type === "all" ? "All Types" : type.replace(/_/g, " ")}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="sortBy">Sort By</Label>
                      <Input
                        type="select"
                        id="sortBy"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="highest">Highest Amount</option>
                        <option value="lowest">Lowest Amount</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4" className="d-flex align-items-end justify-content-center mb-2">
                    <Button
                      color="success"
                      className="btn-rounded waves-effect waves-light mb-2"
                      onClick={() => { window.location.href = 'create-payment' }}
                    >
                      Ajoute un Payement
                    </Button>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label for="searchInput">Search</Label>
                      <Input
                        type="text"
                        id="searchInput"
                        placeholder="Search by name, email, phone"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>                  <Col md="4" className="d-flex align-items-end justify-content-center mb-2">

                  </Col>
                  </Col>
                </Row>

                {isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Oval
                      height={80}
                      width={80}
                      color="#556ee6"
                      visible={true}
                      ariaLabel="oval-loading"
                      secondaryColor="#4fa94d"
                      strokeWidth={2}
                      strokeWidthSecondary={2}
                    />
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="text-center text-muted py-5 border border-light rounded-3">
                    <i className="mdi mdi-cash-remove display-4 d-block mb-2" />
                    <h5 className="mt-3">Aucun paiement</h5>
                    <p className="mb-0">Aucun résultat ne correspond aux filtres.</p>
                  </div>
                ) : (
                  <>
                    <Row>
                      {filteredPayments
                        .slice((page - 1) * pageSize, page * pageSize)
                        .map((payment) => {
                          const typeMap = {
                            RESERVATION: { text: "Voyage", color: "primary", icon: "bx-plane-take-off" },
                            HOTEL_RESERVATION: { text: "Hôtel", color: "success", icon: "bx-bed" },
                            VISA: { text: "Visa", color: "warning", icon: "bxl-visa" },
                          };
                          const typeInfo = typeMap[payment.type] || {
                            text: "Autre",
                            color: "secondary",
                            icon: "bx-cash",
                          };
                          return (
                            <Col xl={4} lg={6} md={6} key={payment.id}>
                              {/* ── RECEIPT CARD — compact, colored stripe + amount ── */}
                              <Card
                                className="h-100 border-0"
                                style={{
                                  borderLeft: `4px solid var(--bs-${typeInfo.color})`,
                                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                                  overflow: "hidden",
                                }}
                              >
                                <CardBody className="p-3">
                                  {/* RECEIPT header */}
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div style={{ minWidth: 0 }} className="flex-grow-1 me-2">
                                      <small
                                        className={`text-${typeInfo.color} text-uppercase fw-bold d-block`}
                                        style={{ letterSpacing: 1.5, fontSize: 10 }}
                                      >
                                        <i className={`bx ${typeInfo.icon} me-1`} />
                                        Reçu · {typeInfo.text}
                                      </small>
                                      <h6 className="mb-0 mt-1 text-truncate">
                                        {payment.firstname} {payment.lastname}
                                      </h6>
                                    </div>
                                    <code
                                      className="text-muted flex-shrink-0"
                                      style={{ fontSize: 10, fontFamily: "monospace" }}
                                    >
                                      #{payment.id?.slice(0, 8).toUpperCase()}
                                    </code>
                                  </div>

                                  {/* Amount block — dashed top/bottom */}
                                  <div
                                    style={{
                                      borderTop: "1px dashed #d1d5db",
                                      borderBottom: "1px dashed #d1d5db",
                                      padding: "8px 0",
                                      margin: "8px 0",
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-baseline mb-1">
                                      <small
                                        className="text-muted text-uppercase fw-medium"
                                        style={{ letterSpacing: 1, fontSize: 9 }}
                                      >
                                        Montant
                                      </small>
                                      <small className="text-muted" style={{ fontSize: 10 }}>
                                        <i className="mdi mdi-calendar me-1" />
                                        {payment.createdAt
                                          ? new Date(payment.createdAt).toLocaleDateString("fr-FR")
                                          : "—"}
                                      </small>
                                    </div>
                                    <h4
                                      className={`text-${typeInfo.color} mb-0`}
                                      style={{ fontWeight: 800, letterSpacing: -0.3 }}
                                    >
                                      {Number(payment.amount || 0).toLocaleString("fr-DZ")}
                                      <small
                                        className="text-muted ms-2"
                                        style={{ fontSize: 11, fontWeight: 500 }}
                                      >
                                        DA
                                      </small>
                                    </h4>
                                  </div>

                                  {/* Contact rows */}
                                  <div style={{ fontSize: 11 }}>
                                    <div className="d-flex justify-content-between text-muted mb-1">
                                      <span className="flex-shrink-0">
                                        <i className="mdi mdi-email-outline me-1" />E-mail
                                      </span>
                                      <span
                                        className="text-end text-truncate text-dark ms-2"
                                        style={{ maxWidth: "65%", fontFamily: "monospace" }}
                                      >
                                        {payment.email || "—"}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between text-muted">
                                      <span><i className="mdi mdi-phone-outline me-1" />Tél.</span>
                                      <span className="text-dark" style={{ fontFamily: "monospace" }}>
                                        {payment.phonenumber || "—"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Footer actions */}
                                  <div className="d-flex justify-content-end gap-3 mt-3 pt-2 border-top">
                                    <Link
                                      to="#"
                                      className="text-primary"
                                      id={`cardview-${payment.id}`}
                                      onClick={() => handleCheckPaymentDetails(payment)}
                                    >
                                      <i className="mdi mdi-eye font-size-18" />
                                      <UncontrolledTooltip placement="top" target={`cardview-${payment.id}`}>
                                        Détails
                                      </UncontrolledTooltip>
                                    </Link>
                                    <Link
                                      to="#"
                                      className="text-danger"
                                      id={`carddel-${payment.id}`}
                                      onClick={() => handleDelete(payment)}
                                    >
                                      <i className="mdi mdi-delete font-size-18" />
                                      <UncontrolledTooltip placement="top" target={`carddel-${payment.id}`}>
                                        Supprimer
                                      </UncontrolledTooltip>
                                    </Link>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                    </Row>
                    <CardPagination
                      page={page}
                      pageSize={pageSize}
                      total={filteredPayments.length}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

Payments.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(Payments)